import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataService } from '../../../services/dataService';
import { Student, LearningPath, Standard } from '../../../data/types';
import Button from '../../ui/Button';
import Badge from '../../ui/Badge';
import Card from '../../ui/Card';
import StudentLayout from '../../layouts/StudentLayout';
import { AuthContext } from '../../../App';

const StudentProgress: React.FC = () => {
  const navigate = useNavigate();
  const { activeRole } = React.useContext(AuthContext);
  const [student, setStudent] = useState<Student | null>(null);
  const [completedStandards, setCompletedStandards] = useState<Standard[]>([]);
  const [inProgressStandards, setInProgressStandards] = useState<Standard[]>([]);
  const [completedPaths, setCompletedPaths] = useState<LearningPath[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Set student session flag when component mounts
  useEffect(() => {
    // Always set the student session flag to true when accessing this page
    localStorage.setItem('isStudentSession', 'true');
    
    // Only redirect if explicitly authenticated as a non-student role
    if (activeRole && activeRole !== 'Student') {
      navigate('/student/login');
      return;
    }
  }, [navigate, activeRole]);
  
  // Ensure the student session flag is set
  useEffect(() => {
    if (!localStorage.getItem('isStudentSession')) {
      localStorage.setItem('isStudentSession', 'true');
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // In a real app, we would get the current student ID from auth context
        // For now, we'll just use the first student from the mock data
        const students = await dataService.getStudents();
        const currentStudent = students[0];
        setStudent(currentStudent);

        // Get standards data
        const allStandards = await dataService.getStandards();
        
        // For demo purposes, randomly assign some standards as completed and in-progress
        const completed = allStandards
          .filter(standard => standard.grade === currentStudent.grade)
          .slice(0, 5); // First 5 standards of student's grade
        
        const inProgress = allStandards
          .filter(standard => standard.grade === currentStudent.grade)
          .slice(5, 8); // Next 3 standards
        
        setCompletedStandards(completed);
        setInProgressStandards(inProgress);
        
        // Get learning paths data
        const allPaths = await dataService.getLearningPaths();
        
        // For demo purposes, mark some paths as completed
        const completedLearningPaths = allPaths
          .filter(path => path.grade === currentStudent.grade)
          .slice(0, 2); // First 2 paths of student's grade
        
        setCompletedPaths(completedLearningPaths);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching progress data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex justify-center items-center h-screen">
          <div className="animate-bounce text-4xl text-purple-600">
            Loading your progress...
          </div>
        </div>
      </StudentLayout>
    );
  }

  if (!student) {
    return (
      <StudentLayout>
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold text-red-500">Oops!</h2>
          <p>We couldn't find your student information. Please try again!</p>
        </div>
      </StudentLayout>
    );
  }

  // Calculate overall progress percentage
  const totalStandards = completedStandards.length + inProgressStandards.length;
  const progressPercentage = totalStandards > 0 
    ? Math.round((completedStandards.length / totalStandards) * 100) 
    : 0;

  return (
    <StudentLayout>
      <div className="p-4 md:p-6">
        {/* Progress Overview */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-500 mb-4">
            My Learning Progress
          </h1>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="p-4 bg-gradient-to-br from-purple-50 to-purple-100">
              <div className="flex items-center">
                <div className="p-3 bg-purple-200 rounded-full mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm text-purple-700 font-medium">Completed Standards</div>
                  <div className="text-2xl font-bold">{completedStandards.length}</div>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100">
              <div className="flex items-center">
                <div className="p-3 bg-blue-200 rounded-full mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm text-blue-700 font-medium">In Progress</div>
                  <div className="text-2xl font-bold">{inProgressStandards.length}</div>
                </div>
              </div>
            </Card>
            
            <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100">
              <div className="flex items-center">
                <div className="p-3 bg-green-200 rounded-full mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm text-green-700 font-medium">Learning Paths</div>
                  <div className="text-2xl font-bold">{completedPaths.length}</div>
                </div>
              </div>
            </Card>
          </div>
          
          <div className="mb-2 flex justify-between items-center">
            <div className="text-lg font-medium">Overall Progress</div>
            <div className="text-lg font-bold">{progressPercentage}%</div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div 
              className="bg-gradient-to-r from-purple-600 to-blue-500 h-4 rounded-full" 
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>
        
        {/* Completed Standards */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center text-purple-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Completed Standards
          </h2>
          
          {completedStandards.length > 0 ? (
            <div className="space-y-3">
              {completedStandards.map(standard => (
                <div key={standard.id} className="p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <Badge variant="success" className="mb-1">{standard.code}</Badge>
                      <div className="text-sm text-gray-600">{standard.description}</div>
                    </div>
                    <div className="text-xs text-green-600 font-medium">
                      Completed on {new Date().toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-4 text-gray-500">
              You haven't completed any standards yet. Keep learning!
            </div>
          )}
        </div>
        
        {/* In Progress Standards */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold mb-4 flex items-center text-blue-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            In Progress
          </h2>
          
          {inProgressStandards.length > 0 ? (
            <div className="space-y-3">
              {inProgressStandards.map(standard => (
                <div key={standard.id} className="p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <Badge variant="primary" className="mb-1">{standard.code}</Badge>
                      <div className="text-sm text-gray-600">{standard.description}</div>
                    </div>
                    <div className="flex items-center">
                      <div className="w-20 bg-gray-200 rounded-full h-2.5 mr-2">
                        <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${Math.floor(Math.random() * 80) + 10}%` }}></div>
                      </div>
                      <Button 
                        variant="primary"
                        size="sm"
                        className="text-xs"
                        onClick={() => navigate(`/student/learning/path-${Math.floor(Math.random() * 100)}/${standard.id}`)}
                      >
                        Continue
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center p-4 text-gray-500">
              You don't have any standards in progress. Start a new learning path!
            </div>
          )}
        </div>
        
        {/* Completed Learning Paths */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center text-green-700">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Completed Learning Paths
          </h2>
          
          {completedPaths.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {completedPaths.map(path => (
                <Card key={path.id} className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-800">{path.name}</h3>
                      <div className="flex items-center mt-1 mb-2">
                        <Badge variant={
                          path.difficulty === 'Easy' ? 'success' : 
                          path.difficulty === 'Medium' ? 'warning' : 'error'
                        } className="mr-2">
                          {path.difficulty}
                        </Badge>
                        <span className="text-xs text-gray-500">Completed on {new Date().toLocaleDateString()}</span>
                      </div>
                      <div className="text-sm text-gray-600 line-clamp-2">{path.description}</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center p-4 text-gray-500">
              You haven't completed any learning paths yet. Keep going!
            </div>
          )}
        </div>
      </div>
    </StudentLayout>
  );
};

export default StudentProgress;
